import { useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, Loader2, Mail, MapPin, Music, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  type StaffSchedule,
  type StaffScheduleShift,
  type StaffScheduleProgramme,
} from '@littlebooker/dal';
import { apiGetPublic } from '@/lib/api';
import { FestivalHeader } from '@/components/timetable/FestivalHeader';
import { ThemeToggle } from '@/components/ThemeToggle';
import { formatDuration, shiftTiming, useNow, type ShiftState } from '@/lib/timetable';

type BannerVariant = 'info' | 'warning' | 'error';

const bannerStyles: Record<BannerVariant, string> = {
  info: 'bg-blue-50 text-blue-900 border-blue-100',
  warning: 'bg-amber-50 text-amber-900 border-amber-100',
  error: 'bg-red-50 text-red-900 border-red-100',
};

function StatusBanner({ title, body, variant }: { title: string; body: string; variant: BannerVariant }) {
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${bannerStyles[variant]}`}>
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-sm/relaxed opacity-90">{body}</p>
      </div>
    </div>
  );
}

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

const dayKey = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const dayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

function PoweredByFooter() {
  return (
    <footer className="mt-10 border-t pt-4 text-center text-xs text-muted-foreground">
      Powered by{' '}
      <a
        href="https://littlestreamer.nl/"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium hover:text-foreground hover:underline"
      >
        LittleBooker
      </a>
    </footer>
  );
}

function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto w-full max-w-2xl px-4 py-8">
        <div className="relative">
          <div className="absolute right-0 top-0 z-10">
            <ThemeToggle />
          </div>
          <div className="space-y-6 pr-12">{children}</div>
        </div>
        <PoweredByFooter />
      </div>
    </div>
  );
}

function ShiftCard({
  shift,
  showProgramme,
  timing,
}: {
  shift: StaffScheduleShift;
  showProgramme: boolean;
  timing: { state: ShiftState; label: string | null };
}) {
  const cardClass =
    timing.state === 'now'
      ? 'border-emerald-500/50 bg-emerald-500/10 ring-1 ring-emerald-500/30'
      : timing.state === 'next'
        ? 'border-primary/40 bg-primary/[0.04]'
        : timing.state === 'past'
          ? 'border-border bg-card opacity-60'
          : 'border-border bg-card';
  const badgeClass =
    timing.state === 'now'
      ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : timing.state === 'next'
        ? 'border-primary/40 bg-primary/10 text-primary'
        : 'border-border bg-muted text-muted-foreground';
  return (
    <div className={`rounded-lg border px-4 py-3 ${cardClass}`}>
      {showProgramme && shift.program_name && (
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{shift.program_name}</p>
      )}
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">
          {fmtTime(shift.start_time)} – {fmtTime(shift.end_time)}{' '}
          <span className="font-normal text-muted-foreground">({formatDuration(shift.start_time, shift.end_time)})</span>
        </span>
        {timing.label && (
          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${badgeClass}`}>
            {timing.state === 'now' && <span className="h-1.5 w-1.5 rounded-full bg-current motion-safe:animate-pulse" />}
            {timing.label}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        <span>{shift.place_name}</span>
        <span className="text-muted-foreground/60">·</span>
        <span className="text-xs uppercase tracking-wide">{shift.role_name}</span>
      </div>
      {shift.performances && shift.performances.length > 0 && (
        <div className="mt-3 space-y-1 border-t pt-3">
          {shift.performances.map((p) => (
            <div key={p.id} className="flex items-start gap-1.5 text-sm">
              <Music className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span>
                {p.start_time && (
                  <span className="text-muted-foreground">{fmtTime(p.start_time)} </span>
                )}
                <span className="font-medium">{p.title}</span>
                {p.artists.length > 0 && (
                  <span className="text-muted-foreground"> — {p.artists.join(', ')}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactBlock({ programme, showProgramme }: { programme: StaffScheduleProgramme; showProgramme: boolean }) {
  const { contact } = programme;
  if (!contact) return null;
  return (
    <div className="text-sm text-muted-foreground">
      <p>
        {showProgramme && <span className="font-medium text-foreground">{programme.name}: </span>}
        {contact.name ? (
          <>
            Shifts put together by <span className="font-medium text-foreground">{contact.name}</span>.
          </>
        ) : (
          'Staffing contact:'
        )}
      </p>
      {(contact.email || contact.phone) && (
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1.5 hover:text-foreground hover:underline">
              <Mail className="h-3.5 w-3.5" />
              {contact.email}
            </a>
          )}
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1.5 hover:text-foreground hover:underline">
              <Phone className="h-3.5 w-3.5" />
              {contact.phone}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function StaffSchedulePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const now = useNow();

  const { data, isLoading, isError, error } = useQuery({
    // Fetch via the public API (server validates the token + bypasses RLS).
    queryKey: ['public-staff-schedule', token],
    queryFn: () => apiGetPublic<StaffSchedule>('/staff-schedule', { token }),
    enabled: Boolean(token),
  });

  const days = useMemo(() => {
    if (!data) return [];
    const groups = new Map<string, { label: string; sort: number; shifts: StaffScheduleShift[] }>();
    for (const shift of data.shifts) {
      const key = dayKey(shift.start_time);
      const group =
        groups.get(key) ??
        groups
          .set(key, { label: dayLabel(shift.start_time), sort: Date.parse(shift.start_time), shifts: [] })
          .get(key)!;
      group.shifts.push(shift);
    }
    return [...groups.values()].sort((a, b) => a.sort - b.sort);
  }, [data]);

  if (!token) {
    return (
      <PageShell>
        <StatusBanner
          variant="warning"
          title="Link incomplete"
          body="This link is missing its access token. Ask your festival organiser to resend it."
        />
      </PageShell>
    );
  }

  if (isLoading) {
    return (
      <PageShell>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your shifts…
        </div>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell>
        <StatusBanner
          variant="error"
          title="Something went wrong"
          body={error instanceof Error ? error.message : 'We could not load your schedule. Try again later.'}
        />
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell>
        <StatusBanner
          variant="error"
          title="Link not valid"
          body="This timetable link isn't recognised. Ask your festival organiser for a fresh link."
        />
      </PageShell>
    );
  }

  const programmeNames = [...new Set(data.shifts.map((s) => s.program_name).filter(Boolean))];
  const shiftCount = data.shifts.length;
  const multiProgramme = programmeNames.length > 1;
  const shiftLabel = shiftCount === 1 ? '1 shift' : `${shiftCount} shifts`;
  const subtitle =
    shiftCount === 0
      ? 'No shifts assigned yet'
      : programmeNames.length === 1
        ? `${shiftLabel} · ${programmeNames[0]}`
        : `${shiftLabel} across ${programmeNames.length} programmes`;

  // Soonest upcoming shift across all days — flagged "Next" in the list.
  const nextShiftId =
    data.shifts
      .filter((s) => Date.parse(s.start_time) > now)
      .sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time))[0]?.id ?? null;

  const eyebrow = data.person.name ? `${data.person.name}'s shifts` : 'Your shifts';
  const soleProgramme = data.programmes.length === 1 ? data.programmes[0] : null;

  return (
    <PageShell>
      {soleProgramme ? (
        <div className="space-y-1">
          <FestivalHeader
            name={soleProgramme.name}
            startTime={soleProgramme.start_time}
            endTime={soleProgramme.end_time}
            location={soleProgramme.location}
            eyebrow={eyebrow}
          />
          <p className="text-sm text-muted-foreground">{shiftLabel}</p>
        </div>
      ) : (
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">{eyebrow}</h1>
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <CalendarClock className="h-4 w-4" />
            {subtitle}
          </p>
        </div>
      )}

      {data.programmes.some((p) => p.contact) && (
        <div className="space-y-3 rounded-lg border bg-muted/30 px-4 py-3">
          {data.programmes
            .filter((p) => p.contact)
            .map((p) => (
              <ContactBlock key={p.id} programme={p} showProgramme={multiProgramme} />
            ))}
        </div>
      )}

      {days.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            You don't have any shifts assigned yet. Check back later.
          </CardContent>
        </Card>
      ) : (
        days.map((day) => (
          <Card key={day.label}>
            <CardHeader>
              <CardTitle className="text-base">{day.label}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {day.shifts.map((shift) => (
                <ShiftCard
                  key={shift.id}
                  shift={shift}
                  showProgramme={multiProgramme}
                  timing={shiftTiming(now, shift.start_time, shift.end_time, shift.id === nextShiftId)}
                />
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </PageShell>
  );
}
